import { Segmented, Button, DatePicker, Select, Space } from 'antd';
import { LeftOutlined, RightOutlined, StopOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { SchedView, TechLane } from '../useSchedulingData';

interface ScheduleControlsProps {
  view: SchedView;
  onView: (v: SchedView) => void;
  anchor: Dayjs;
  onAnchor: (d: Dayjs) => void;
  techs: TechLane[];
  technicianId?: number;
  onTech: (id?: number) => void;
  /** When provided (caller has manage_schedule), shows a "Block time" button
   *  that opens the BlockTimeDrawer with no prefilled tech/time. */
  onBlockTime?: () => void;
}

// SP-1 Task 7: Day, Agenda, Week, Month — Cards removed from the switcher
// (CardsView/EmployeeCardGrid stay on disk, just unlinked from here).
const VIEW_OPTIONS: { label: string; value: SchedView }[] = [
  { label: 'Day', value: 'day' },
  { label: 'Agenda', value: 'agenda' },
  { label: 'Week', value: 'week' },
  { label: 'Month', value: 'month' },
];

function step(anchor: Dayjs, view: SchedView, dir: 1 | -1): Dayjs {
  // Agenda is a day-to-day list — step it one day at a time (like Day view),
  // not a week, so the prev/next arrows reach every day.
  const unit = view === 'month' ? 'month' : view === 'week' ? 'week' : 'day';
  return dir === 1 ? anchor.add(1, unit) : anchor.subtract(1, unit);
}

export function ScheduleControls({
  view,
  onView,
  anchor,
  onAnchor,
  techs,
  technicianId,
  onTech,
  onBlockTime,
}: ScheduleControlsProps) {
  return (
    <Space wrap style={{ width: '100%', justifyContent: 'space-between' }} data-testid="schedule-controls">
      <Segmented
        value={view}
        onChange={(val) => onView(val as SchedView)}
        options={VIEW_OPTIONS}
      />
      {onBlockTime && (
        <Button icon={<StopOutlined />} onClick={onBlockTime} data-testid="block-time-btn">
          Block time
        </Button>
      )}
      <Space.Compact>
        <Button icon={<LeftOutlined />} onClick={() => onAnchor(step(anchor, view, -1))} aria-label="Previous" />
        <Button onClick={() => onAnchor(dayjs())}>Today</Button>
        <DatePicker value={anchor} onChange={(d) => d && onAnchor(d)} allowClear={false} />
        <Button icon={<RightOutlined />} onClick={() => onAnchor(step(anchor, view, 1))} aria-label="Next" />
      </Space.Compact>
      <Select
        allowClear
        placeholder="All technicians"
        style={{ minWidth: 180 }}
        value={technicianId}
        onChange={(val) => onTech(val ?? undefined)}
        options={techs.map((t) => ({ label: t.name, value: t.id }))}
      />
    </Space>
  );
}

export default ScheduleControls;
